import { useState, useCallback, useEffect } from "react";

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface ImageOffset {
  x: number;
  y: number;
}

interface UseImageCropperProps {
  image: HTMLImageElement | null;
  containerWidth: number;
  containerHeight: number;
  aspectRatio?: number; // width / height, default 1 (square)
}

export const useImageCropper = ({
  image,
  containerWidth,
  containerHeight,
  aspectRatio = 1,
}: UseImageCropperProps) => {
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    width: 0,
    height: 0,
  });
  const [imageOffset, setImageOffset] = useState<ImageOffset>({ x: 0, y: 0 });

  // Calculate scaled image dimensions to fit container while maintaining aspect ratio
  useEffect(() => {
    if (!image || !containerWidth || !containerHeight) return;

    const imgAspect = image.width / image.height;
    const containerAspect = containerWidth / containerHeight;

    let displayWidth: number;
    let displayHeight: number;

    if (imgAspect > containerAspect) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / imgAspect;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * imgAspect;
    }

    // Apply zoom
    displayWidth *= zoom;
    displayHeight *= zoom;

    // Calculate image offset (how much the image is offset from top-left of container when centered)
    const offsetX = (containerWidth - displayWidth) / 2;
    const offsetY = (containerHeight - displayHeight) / 2;

    setImageDimensions({ width: displayWidth, height: displayHeight });
    setImageOffset({ x: offsetX, y: offsetY });

    // Initialize crop area in the center, respecting aspect ratio
    let cropWidth: number;
    let cropHeight: number;
    if (displayWidth / aspectRatio <= displayHeight) {
      cropWidth = displayWidth * 0.8;
      cropHeight = cropWidth / aspectRatio;
    } else {
      cropHeight = displayHeight * 0.8;
      cropWidth = cropHeight * aspectRatio;
    }
    setCropArea({
      x: (displayWidth - cropWidth) / 2,
      y: (displayHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    });
  }, [image, containerWidth, containerHeight, zoom, aspectRatio]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: "drag" | "resize") => {
      e.preventDefault();
      e.stopPropagation();

      if (type === "drag") {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - imageOffset.x - cropArea.x,
          y: e.clientY - imageOffset.y - cropArea.y
        });
      } else {
        setIsResizing(true);
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    },
    [cropArea.x, cropArea.y, imageOffset.x, imageOffset.y]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(
          0,
          Math.min(e.clientX - imageOffset.x - dragStart.x, imageDimensions.width - cropArea.width)
        );
        const newY = Math.max(
          0,
          Math.min(e.clientY - imageOffset.y - dragStart.y, imageDimensions.height - cropArea.height)
        );
        setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
      } else if (isResizing) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        const delta = Math.max(deltaX, deltaY);

        const rawWidth = Math.max(50, Math.min(cropArea.width + delta, imageDimensions.width - cropArea.x));
        const rawHeight = rawWidth / aspectRatio;
        // Constrain height within bounds
        const actualHeight = Math.min(rawHeight, imageDimensions.height - cropArea.y);
        const actualWidth = actualHeight * aspectRatio;

        setCropArea((prev) => ({ ...prev, width: actualWidth, height: actualHeight }));
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    },
    [
      isDragging,
      isResizing,
      dragStart,
      cropArea,
      imageDimensions.width,
      imageDimensions.height,
      imageOffset.x,
      imageOffset.y,
      aspectRatio,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Touch event handlers for mobile support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, type: "drag" | "resize") => {
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];

      if (type === "drag") {
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - imageOffset.x - cropArea.x,
          y: touch.clientY - imageOffset.y - cropArea.y
        });
      } else {
        setIsResizing(true);
        setDragStart({ x: touch.clientX, y: touch.clientY });
      }
    },
    [cropArea.x, cropArea.y, imageOffset.x, imageOffset.y]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging && !isResizing) return;

      const touch = e.touches[0];
      if (isDragging) {
        const newX = Math.max(
          0,
          Math.min(touch.clientX - imageOffset.x - dragStart.x, imageDimensions.width - cropArea.width)
        );
        const newY = Math.max(
          0,
          Math.min(touch.clientY - imageOffset.y - dragStart.y, imageDimensions.height - cropArea.height)
        );
        setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
      } else if (isResizing) {
        const deltaX = touch.clientX - dragStart.x;
        const deltaY = touch.clientY - dragStart.y;
        const delta = Math.max(deltaX, deltaY);

        const rawWidth = Math.max(50, Math.min(cropArea.width + delta, imageDimensions.width - cropArea.x));
        const rawHeight = rawWidth / aspectRatio;
        const actualHeight = Math.min(rawHeight, imageDimensions.height - cropArea.y);
        const actualWidth = actualHeight * aspectRatio;

        setCropArea((prev) => ({ ...prev, width: actualWidth, height: actualHeight }));
        setDragStart({ x: touch.clientX, y: touch.clientY });
      }
    },
    [
      isDragging,
      isResizing,
      dragStart,
      cropArea,
      imageDimensions.width,
      imageDimensions.height,
      imageOffset.x,
      imageOffset.y,
      aspectRatio,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleTouchEnd);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(Math.max(0.5, Math.min(3, newZoom)));
  }, []);

  const getCroppedImage = useCallback(async (): Promise<Blob | null> => {
    if (!image || !imageDimensions.width) return null;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Calculate scale between displayed image and original image
    const scaleX = image.width / imageDimensions.width;
    const scaleY = image.height / imageDimensions.height;

    // Convert crop coordinates from display space to original image space
    const cropX = cropArea.x * scaleX;
    const cropY = cropArea.y * scaleY;
    const cropW = cropArea.width * scaleX;
    const cropH = cropArea.height * scaleY;

    // Set canvas size (max 1200px wide to preserve quality for banners)
    const maxOutputWidth = 1200;
    const outputWidth = Math.min(cropW, maxOutputWidth);
    const outputHeight = outputWidth / aspectRatio;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, outputWidth, outputHeight);

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    });
  }, [image, cropArea, imageDimensions, aspectRatio]);

  return {
    cropArea,
    zoom,
    imageDimensions,
    imageOffset,
    isDragging,
    isResizing,
    handleMouseDown,
    handleTouchStart,
    handleZoomChange,
    getCroppedImage,
  };
};
